<template>
    <b-container fluid class="eval-profile-management">
        <admin-page-header title="AI 평가 프로파일"
                           description="질문지별로 평가자의 평가 기준을 등록해 AI 평가에 적용합니다. 작성하지 않은 항목은 시스템 기본 기준이 적용됩니다.">
            <template #actions>
                <b-button variant="primary" @click="openCreateModal">
                    <AppIcon name="plus" class="mr-1"/>새 프로파일
                </b-button>
            </template>
        </admin-page-header>

        <b-card no-body class="p-3">
            <div class="d-flex align-items-center mb-3">
                <b-form-select
                    v-model="statusFilter"
                    :options="statusFilterOptions"
                    aria-label="상태 필터"
                    style="max-width: 200px"
                    @change="onStatusChange"
                />
                <span v-if="!loading && !loadError" class="ml-3 text-muted">총 {{ profiles.length }}개</span>
            </div>

            <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title. 폭은 머리가 정한다 -->
            <b-table
                :items="profiles"
                :fields="fields"
                :busy="loading"
                table-class="ds-table ds-table-fixed eval-profile-table"
                hover
                responsive
                show-empty
            >
                <template #empty>
                    <PageEmptyComponent
                        compact
                        :error="loadError ? '프로파일 목록을 불러오지 못했습니다.' : ''"
                        :text="statusFilter ? '이 상태의 평가 프로파일이 없습니다.' : '등록된 평가 프로파일이 없습니다.'"
                        @retry="fetchProfiles"
                    />
                </template>
                <template #table-busy>
                    <div class="text-center text-muted my-3">
                        <b-spinner small class="align-middle mr-2" />
                        <span class="align-middle">불러오는 중...</span>
                    </div>
                </template>

                <template #cell(no)="row">{{ row.index + 1 }}</template>

                <!-- 메인 칸 = 편집 폼(?profile= 로 열린다 — 예전처럼 히스토리를 늘리지 않게 replace) -->
                <template #cell(profile_name)="row">
                    <span class="ds-ellipsis" :title="row.value"><router-link :to="editRoute(row.item)" replace class="ds-detail-link">{{ row.value }}</router-link></span>
                </template>

                <template #cell(status)="row">
                    <span class="ds-status" :class="`ds-status-${statusVariant(row.value)}`">{{ statusLabel(row.value) }}</span>
                </template>

                <!-- 첫 연결 + 외 N 한 줄, 전체 목록은 title -->
                <template #cell(linked_projects)="row">
                    <span v-if="row.value.length" class="linked-projects" :title="linkedNames(row.value)">
                        <span class="ds-tag"><span class="ds-ellipsis">{{ row.value[0].label || row.value[0].project_code }}</span></span> <span v-if="row.value.length > 1" class="ds-sub">외 {{ row.value.length - 1 }}</span>
                    </span>
                    <span v-else class="ds-sub">-</span>
                </template>

                <template #cell(updated_at)="row">
                    {{ formatDate(row.item.updated_at || row.item.created_at) }}
                </template>

                <!-- 행 동작(아이콘 28): 비교 실행 · 상태 변경·삭제 · 편집(이름 링크와 같은 곳) -->
                <template #cell(actions)="row">
                    <div class="d-flex justify-content-end align-items-center" style="gap: 4px">
                        <button type="button" class="ds-row-icon-btn" :aria-label="`${row.item.profile_name} 비교 실행`"
                                title="비교 실행" @click="openCompare(row.item)">
                            <AppIcon name="arrows-left-right"/>
                        </button>
                        <b-dropdown variant="link" no-caret right boundary="viewport"
                                    toggle-class="ds-row-icon-btn"
                                    :toggle-attrs="{ 'aria-label': `${row.item.profile_name} 상태 변경·삭제`, title: '상태 변경·삭제' }"
                                    :disabled="statusChangingId !== null">
                            <template #button-content><AppIcon name="dots-vertical"/></template>
                            <b-dropdown-item v-if="row.item.status === 'draft'"
                                             @click="changeStatus(row.item, 'active')">
                                운영 상태로 적용
                            </b-dropdown-item>
                            <b-dropdown-item v-if="row.item.status === 'active'"
                                             @click="changeStatus(row.item, 'archived')">
                                보관 처리
                            </b-dropdown-item>
                            <b-dropdown-item v-if="row.item.status === 'archived'"
                                             @click="changeStatus(row.item, 'draft')">
                                초안으로 복원
                            </b-dropdown-item>
                            <b-dropdown-divider />
                            <b-dropdown-item variant="danger" :disabled="deletingId !== null" @click="confirmDelete(row.item)">
                                삭제
                            </b-dropdown-item>
                        </b-dropdown>
                        <router-link :to="editRoute(row.item)" replace class="ds-row-icon-btn"
                                     :aria-label="`${row.item.profile_name} 편집`" title="편집">
                            <AppIcon name="chevron-right"/>
                        </router-link>
                    </div>
                </template>
            </b-table>
        </b-card>

        <!-- 생성 모달 -->
        <!-- 생성 모달: 실패해도 닫지 않고 입력을 남긴다. 생성 중에는 닫기 잠금 -->
        <b-modal id="create-profile-modal" title="새 평가 프로파일" ok-title="생성"
                 cancel-title="취소" :busy="creating" :no-close-on-backdrop="creating"
                 :no-close-on-esc="creating" :hide-header-close="creating" @ok="createProfile">
            <b-form @submit.prevent="createProfile">
                <b-form-group label="프로파일 이름" label-for="new-profile-name">
                    <b-form-input id="new-profile-name" v-model="newProfileName"
                                  :state="createSubmitted && !newProfileName.trim() ? false : null"
                                  placeholder="예: 공급망 평가 기준 v2" autofocus />
                    <b-form-invalid-feedback>프로파일 이름을 입력해 주세요.</b-form-invalid-feedback>
                </b-form-group>
            </b-form>
            <b-alert :show="!!createError" variant="danger" class="mb-2">{{ createError }}</b-alert>
            <p class="text-muted small mb-0">
                생성 후 편집 화면에서 질문에 답하면 평가 기준이 만들어집니다.
            </p>
        </b-modal>

        <!-- 작성 폼 (편집) -->
        <EvalProfileForm
            v-if="editingProfileId"
            ref="profileForm"
            :key="editingProfileId"
            :profile-id="editingProfileId"
            @saved="onFormSaved"
            @close="closeEditForm"
        />

        <!-- 비교 실행 -->
        <EvalProfileCompare
            v-if="compareProfile"
            :base-profile="compareProfile"
            :profiles="profiles"
            @close="compareProfile = null"
        />
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import EvalProfileForm from './EvalProfileForm.vue';
import EvalProfileCompare from './EvalProfileCompare.vue';

const STATUS_VALUES = ['draft', 'active', 'archived'];
const first = (v) => (Array.isArray(v) ? v[0] : v);
// URL(?status=&profile=) → 화면 상태. 화이트리스트·양의 정수만 받고 나머지는 기본값.
function parseQuery(query) {
    const status = first(query.status);
    const profile = String(first(query.profile) ?? '');
    return {
        status: STATUS_VALUES.includes(status) ? status : '',
        profile: /^[1-9]\d{0,9}$/.test(profile) ? Number(profile) : null,
    };
}

export default {
    name: 'EvalProfileManagement',
    components: { EvalProfileForm, EvalProfileCompare, PageEmptyComponent },
    // 편집 폼에 저장하지 않은 변경이 있으면 이 화면을 벗어나기 전에 확인한다.
    beforeRouteLeave(to, from, next) {
        this.confirmCloseForm().then((ok) => next(ok ? undefined : false));
    },
    // 같은 화면에서 URL 이 바뀌어 편집 폼이 닫히게 되면(뒤로가기 등) 먼저 확인한다. 적용은 $route.query watcher.
    beforeRouteUpdate(to, from, next) {
        if (parseQuery(to.query).profile === this.editingProfileId) return next();
        this.confirmCloseForm().then((ok) => next(ok ? undefined : false));
    },
    data() {
        return {
            profiles: [],
            loading: false,
            loadError: false,
            fetchReq: 0,
            deletingId: null,
            statusChangingId: null,
            statusFilter: '',
            newProfileName: '',
            creating: false,
            createSubmitted: false,
            createError: '',
            editingProfileId: null,
            compareProfile: null,
            statusFilterOptions: [
                { value: '', text: '전체 상태' },
                { value: 'draft', text: '초안 (draft)' },
                { value: 'active', text: '운영 (active)' },
                { value: 'archived', text: '보관 (archived)' },
            ],
            // 폭은 머리가 정한다(ds-table-fixed) — 폭을 안 적은 프로파일명 · 연결 프로젝트가 남는 폭을 나눠 갖는다
            fields: [
                { key: 'no', label: 'No.', thClass: 'ds-cell-no', tdClass: 'ds-cell-no', thStyle: { width: '56px' } },
                { key: 'profile_name', label: '프로파일명' },
                { key: 'status', label: '상태', thStyle: { width: '96px' } },
                { key: 'linked_projects', label: '연결 프로젝트' },
                { key: 'version', label: '버전', thClass: 'text-right', tdClass: 'ds-cell-num', thStyle: { width: '72px' } },
                { key: 'updated_at', label: '최종 수정', tdClass: 'ds-cell-nowrap', thStyle: { width: '140px' } },
                { key: 'actions', label: '관리', thClass: 'text-right', thStyle: { width: '124px' } },
            ],
        };
    },
    watch: {
        // 뒤로가기·사이드바 재클릭·링크로 URL 이 바뀌면 화면을 URL 에 맞춘다.
        // 화면이 직접 바꾼 URL 은 이미 같은 상태라 다시 불러오지 않는다.
        '$route.query'(query) {
            this.applyQuery(query);
        },
    },
    created() {
        this.applyQuery(this.$route.query, true);
    },
    methods: {
        applyQuery(query, force = false) {
            const { status, profile } = parseQuery(query);
            if (force || status !== this.statusFilter) {
                this.statusFilter = status;
                this.fetchProfiles();
            }
            // 목록에 없는 id 여도 연다 — 상태 필터 밖이거나 막 만든 프로파일일 수 있고, 없는 id 는 폼이 불러오기 오류로 보여준다.
            this.editingProfileId = profile;
        },
        // 기본값·빈 값은 생략: status 는 필터를 고른 경우만, profile 은 편집 폼이 열려 있을 때만.
        buildQuery() {
            const query = {};
            if (this.statusFilter) query.status = this.statusFilter;
            if (this.editingProfileId) query.profile = String(this.editingProfileId);
            return query;
        },
        // 사용자 조작(필터 변경)은 push, 편집 폼 열기/닫기는 히스토리를 늘리지 않게 replace.
        syncQuery(method) {
            const next = parseQuery(this.buildQuery());
            const cur = parseQuery(this.$route.query);
            if (next.status === cur.status && next.profile === cur.profile) return;
            this.$router[method]({ query: this.buildQuery() }).catch(() => {});
        },
        // 편집 폼이 열려 있고 미저장 변경이 있으면 확인창(취소 = false). 폼이 없으면 true.
        confirmCloseForm() {
            const form = this.$refs.profileForm;
            return form ? form.confirmDiscardChanges() : Promise.resolve(true);
        },
        onStatusChange() {
            this.syncQuery('push');
            this.fetchProfiles();
        },
        async fetchProfiles() {
            const req = ++this.fetchReq;  // 필터를 연달아 바꿨을 때 늦게 온 이전 응답은 버린다
            this.loading = true;
            this.loadError = false;
            try {
                const params = this.statusFilter ? { status: this.statusFilter } : {};
                const { data } = await this.$axios.get('/eval-profiles', { params });
                if (req !== this.fetchReq) return;
                this.profiles = data;
            } catch (e) {
                if (req !== this.fetchReq) return;
                // 이전 목록을 남기면 '없음' 대신 다른 상태의 목록이 보이므로 비운다
                this.profiles = [];
                this.loadError = true;
                if (e?.response?.status === 401) return;
                this.$bvToast.toast('프로파일 목록을 불러오지 못했습니다.', {
                    title: '오류', variant: 'danger',
                });
            } finally {
                if (req === this.fetchReq) this.loading = false;
            }
        },
        openCreateModal() {
            this.newProfileName = '';
            this.createSubmitted = false;
            this.createError = '';
            this.$bvModal.show('create-profile-modal');
        },
        // [생성]·Enter 공통. 모달은 성공했을 때만 닫는다.
        async createProfile(evt) {
            if (evt) evt.preventDefault();
            if (this.creating) return;
            this.createSubmitted = true;
            this.createError = '';
            const name = (this.newProfileName || '').trim();
            if (!name) return;
            this.creating = true;
            try {
                const { data } = await this.$axios.post('/eval-profiles', {
                    profile_name: name,
                });
                this.$bvModal.hide('create-profile-modal');
                await this.fetchProfiles();
                // 생성 직후 바로 편집 폼 오픈
                this.openEditForm(data);
            } catch (e) {
                if (e?.response?.status === 401) return;
                this.createError = getErrorMessage(e, '프로파일 생성에 실패했습니다.');
            } finally {
                this.creating = false;
            }
        },
        // 목록의 편집 링크 — 지금 필터는 두고 profile 만 바꾼다. 적용은 $route.query watcher(openEditForm 과 같은 결과)
        editRoute(profile) {
            return { query: { ...this.buildQuery(), profile: String(profile.id) } };
        },
        linkedNames(linked) {
            return linked.map((lp) => lp.label || lp.project_code).join(', ');
        },
        openEditForm(profile) {
            this.editingProfileId = profile.id;
            this.syncQuery('replace');
        },
        closeEditForm() {
            this.editingProfileId = null;
            this.syncQuery('replace');
        },
        openCompare(profile) {
            this.compareProfile = profile;
        },
        onFormSaved() {
            // 저장 후 편집 모달을 닫고 목록을 갱신한다.
            this.closeEditForm();
            this.fetchProfiles();
        },
        // 운영 적용·보관은 연결 프로젝트의 AI 평가 기준을 바로 바꾸므로, 연결이 있으면 확인받는다.
        async changeStatus(profile, newStatus) {
            if (this.statusChangingId !== null) return;
            const linked = profile.linked_projects || [];
            if (linked.length && (newStatus === 'active' || newStatus === 'archived')) {
                const names = linked.map((lp) => lp.label || lp.project_code).join(', ');
                const effect = newStatus === 'active'
                    ? '의 AI 평가에 다음 요청부터 바로 적용됩니다.'
                    : '는 시스템 기본 기준으로 평가됩니다.';
                const ok = await this.$showModal({
                    type: 'confirm',
                    state: 'warning',
                    title: newStatus === 'active' ? '운영 상태로 적용' : '프로파일 보관',
                    modalOptions: { okTitle: newStatus === 'active' ? '운영 적용' : '보관' },
                    message: `「${profile.profile_name}」을(를) ${newStatus === 'active' ? '운영 상태로 바꿉니다' : '보관합니다'}. `
                        + `연결된 프로젝트 ${linked.length}개(${names}) 중 이 프로파일을 기본 기준으로 쓰는 프로젝트${effect}`,
                });
                if (!ok) return;
            }
            this.statusChangingId = profile.id;
            try {
                await this.$axios.patch(`/eval-profiles/${profile.id}`, { status: newStatus });
                await this.fetchProfiles();
            } catch (e) {
                if (e?.response?.status === 401) return;
                const msg = getErrorMessage(e, '상태 변경에 실패했습니다.');
                this.$bvToast.toast(msg, { title: '오류', variant: 'danger' });
            } finally {
                this.statusChangingId = null;
            }
        },
        async confirmDelete(profile) {
            if (this.deletingId) return;
            const h = this.$createElement;
            const linked = profile.linked_projects || [];
            const names = linked.map((lp) => lp.label || lp.project_code).join(', ');
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'error',
                title: '평가 프로파일 삭제',
                modalOptions: { okTitle: '삭제' },
                message: h('div', { class: 'text-secondary' }, [
                    h('p', { class: 'mb-2' }, `'${profile.profile_name}' (${this.statusLabel(profile.status)}) 프로파일을 삭제합니다. 답변과 질문지 연결이 함께 삭제됩니다.`),
                    linked.length
                        ? h('p', { class: 'mb-2' }, `연결된 프로젝트 ${linked.length}개: ${names}. 이 프로젝트들은 시스템 기본 기준으로 평가됩니다.`)
                        : h('p', { class: 'mb-2' }, '연결된 프로젝트가 없습니다.'),
                    h('p', { class: 'mb-0 text-danger' }, '삭제하면 되돌릴 수 없습니다.'),
                ]),
            });
            if (!ok) return;
            this.deletingId = profile.id;
            try {
                await this.$axios.delete(`/eval-profiles/${profile.id}`);
                await this.fetchProfiles();
            } catch (e) {
                if (e?.response?.status === 401) return;
                this.$bvToast.toast(getErrorMessage(e, '삭제에 실패했습니다.'), { title: '오류', variant: 'danger' });
            } finally {
                this.deletingId = null;
            }
        },
        statusLabel(status) {
            return { draft: '초안', active: '운영', archived: '보관' }[status] || status;
        },
        statusVariant(status) {
            return { draft: 'secondary', active: 'success', archived: 'light' }[status] || 'secondary';
        },
        formatDate(iso) {
            if (!iso) return '-';
            return String(iso).replace('T', ' ').slice(0, 16);
        },
    },
};
</script>

<style scoped>
.eval-profile-management h4 {
    font-weight: 700;
}

/* 고정 칸 합 488(No. 56 · 상태 96 · 버전 72 · 최종 수정 140 · 관리 124) + 프로파일명 · 연결 프로젝트 ~206씩.
   1366(메뉴 펼침) 본문 약 1074 에서 카드 p-3 · 테두리를 뺀 약 1040 안 — 그보다 좁으면 가로 스크롤 */
::v-deep .eval-profile-table {
    min-width: 900px;
}

/* 첫 연결 이름이 길면 배지 안에서 줄이고 '외 N' 은 늘 보이게 */
.linked-projects {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
}
.linked-projects .ds-tag {
    min-width: 0;
}
/* ds-tag 의 line-height 1 그대로면 overflow hidden 에 아래 획(p, g)이 잘린다 */
.linked-projects .ds-tag .ds-ellipsis {
    line-height: 1.4;
}
.linked-projects .ds-sub {
    flex-shrink: 0;
    white-space: nowrap;
}
</style>
